import { pgTable, text, serial, integer, boolean, date, timestamp } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const bookings = pgTable("bookings", {
  id: serial("id").primaryKey(),
  bookingReference: text("booking_reference").notNull(),
  name: text("name").notNull(),
  email: text("email").notNull(),
  phone: text("phone").notNull(),
  tourDate: date("tour_date").notNull(),
  participants: integer("participants").notNull(),
  tourName: text("tour_name"),
  message: text("message"),
  status: text("status").notNull().default("pending"),
  totalAmount: integer("total_amount"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertBookingSchema = createInsertSchema(bookings)
  .pick({
    name: true,
    email: true,
    phone: true,
    tourDate: true,
    participants: true,
    tourName: true,
    message: true,
  })
  .extend({
    email: z.string().email("Invalid email address"),
    phone: z.string().min(10, "Phone number must be at least 10 digits"),
    participants: z.number().min(1, "Must have at least 1 participant").max(20, "Maximum 20 participants per booking"),
    tourDate: z.coerce.date().min(new Date(), "Tour date must be in the future"),
    name: z.string().min(2, "Name must be at least 2 characters"),
    tourName: z.string().optional(),
  });

export type InsertBooking = z.infer<typeof insertBookingSchema>;
export type Booking = typeof bookings.$inferSelect;

// Booking status types
export const BookingStatus = {
  PENDING: "pending",
  CONFIRMED: "confirmed",
  CANCELLED: "cancelled",
  COMPLETED: "completed",
} as const;

export type BookingStatus = typeof BookingStatus[keyof typeof BookingStatus];

// Wishlist schema
export const wishlist = pgTable("wishlist", {
  id: serial("id").primaryKey(),
  userId: text("user_id").notNull(), // For future authentication implementation or session ID
  userEmail: text("user_email"), // For guest identification
  userName: text("user_name"), // For guest identification
  tourId: text("tour_id").notNull(), // Tour identifier (e.g., 'uganda/murchison-falls-3-day')
  tourName: text("tour_name").notNull(),
  tourDescription: text("tour_description"),
  tourImage: text("tour_image").notNull(),
  tourHref: text("tour_href"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertWishlistSchema = createInsertSchema(wishlist)
  .pick({
    userId: true,
    userEmail: true,
    userName: true,
    tourId: true,
    tourName: true,
    tourDescription: true,
    tourImage: true,
    tourHref: true,
  })
  .extend({
    userId: z.string().min(1, "User ID is required"),
    userEmail: z.string().email("Invalid email").optional().or(z.literal('')),
    userName: z.string().optional(),
    tourId: z.string().min(1, "Tour ID is required"),
    tourName: z.string().min(1, "Tour name is required"),
    tourDescription: z.string().optional(),
    tourImage: z.string().min(1, "Tour image is required"),
    tourHref: z.string().optional(),
  });

export type InsertWishlist = z.infer<typeof insertWishlistSchema>;
export type Wishlist = typeof wishlist.$inferSelect;

// EcoDb Contacts schema with enhanced CRM features
export const contacts = pgTable("contacts", {
  id: serial("id").primaryKey(),
  fullName: text("full_name").notNull(),
  email: text("email").notNull().unique(),
  dateOfBirth: date("date_of_birth").notNull(),
  country: text("country").notNull(),
  // Travel preferences
  travelTypes: text("travel_types").array().default([]), // Beach Holiday, Safari, City Tour, etc.
  favoriteDestinations: text("favorite_destinations"), // Free text field
  lastTripDate: date("last_trip_date"),
  // System fields
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

// Notes and interaction history table
export const contactNotes = pgTable("contact_notes", {
  id: serial("id").primaryKey(),
  contactId: integer("contact_id").references(() => contacts.id, { onDelete: "cascade" }).notNull(),
  noteType: text("note_type").notNull(), // 'note', 'call', 'email', 'meeting'
  content: text("content").notNull(),
  interactionDate: timestamp("interaction_date").defaultNow(),
  createdBy: text("created_by").default("Admin"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

// Travel offers table for bulk email campaigns
export const travelOffers = pgTable("travel_offers", {
  id: serial("id").primaryKey(),
  title: text("title").notNull(),
  description: text("description").notNull(),
  imageUrl: text("image_url"),
  price: text("price"), // Store as text for flexibility (e.g., "From $1,200")
  bookingLink: text("booking_link"),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertContactSchema = createInsertSchema(contacts)
  .pick({
    fullName: true,
    email: true,
    dateOfBirth: true,
    country: true,
    travelTypes: true,
    favoriteDestinations: true,
    lastTripDate: true,
  })
  .extend({
    fullName: z.string().min(2, "Full name must be at least 2 characters").max(100, "Full name must be less than 100 characters"),
    email: z.string().email("Invalid email address"),
    dateOfBirth: z.coerce.date().max(new Date(), "Date of birth cannot be in the future"),
    country: z.string().min(2, "Country is required"),
    travelTypes: z.array(z.string()).optional().default([]),
    favoriteDestinations: z.string().optional(),
    lastTripDate: z.coerce.date().optional(),
  });

export const insertContactNoteSchema = createInsertSchema(contactNotes)
  .pick({
    contactId: true,
    noteType: true,
    content: true,
    interactionDate: true,
  })
  .extend({
    contactId: z.number().min(1, "Contact ID is required"),
    noteType: z.enum(['note', 'call', 'email', 'meeting'], { required_error: "Note type is required" }),
    content: z.string().min(1, "Content is required").max(1000, "Content must be less than 1000 characters"),
    interactionDate: z.coerce.date().optional(),
  });

export const insertTravelOfferSchema = createInsertSchema(travelOffers)
  .pick({
    title: true,
    description: true,
    imageUrl: true,
    price: true,
    bookingLink: true,
  })
  .extend({
    title: z.string().min(1, "Title is required").max(255, "Title must be less than 255 characters"),
    description: z.string().min(1, "Description is required"),
    imageUrl: z.string().url("Invalid image URL").optional(),
    price: z.string().optional(),
    bookingLink: z.string().url("Invalid booking link").optional(),
  });

export type InsertContact = z.infer<typeof insertContactSchema>;
export type Contact = typeof contacts.$inferSelect;
export type InsertContactNote = z.infer<typeof insertContactNoteSchema>;
export type ContactNote = typeof contactNotes.$inferSelect;
export type InsertTravelOffer = z.infer<typeof insertTravelOfferSchema>;
export type TravelOffer = typeof travelOffers.$inferSelect;

// Website leads for CRM tracking
export const websiteLeads = pgTable("website_leads", {
  id: serial("id").primaryKey(),
  email: text("email").notNull().unique(),
  name: text("name"),
  phone: text("phone"),
  source: text("source").notNull().default("unknown"), // e.g., 'Lead Capture Form', 'Booking Page'
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertLeadSchema = createInsertSchema(websiteLeads)
  .pick({
    email: true,
    name: true,
    phone: true,
    source: true,
  })
  .extend({
    email: z.string().email("Invalid email address"),
    name: z.string().optional(),
    phone: z.string().optional(),
    source: z.string().optional(),
  });

export type InsertLead = z.infer<typeof insertLeadSchema>;
export type WebsiteLead = typeof websiteLeads.$inferSelect;

// Employees table for internal staff tracking and birthdays
export const employees = pgTable("employees", {
  id: serial("id").primaryKey(),
  fullName: text("full_name").notNull(),
  email: text("email").notNull().unique(),
  phone: text("phone"),
  dateOfBirth: date("date_of_birth").notNull(),
  jobTitle: text("job_title"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertEmployeeSchema = createInsertSchema(employees)
  .pick({
    fullName: true,
    email: true,
    dateOfBirth: true,
    jobTitle: true,
  })
  .extend({
    fullName: z.string().min(2, "Full name must be at least 2 characters").max(100, "Full name must be less than 100 characters"),
    email: z.string().email("Invalid email address"),
    dateOfBirth: z.coerce.date().max(new Date(), "Date of birth cannot be in the future"),
    jobTitle: z.string().optional(),
  });

export type InsertEmployee = z.infer<typeof insertEmployeeSchema>;
export type Employee = typeof employees.$inferSelect;